import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import Leftsidebar from "../sidebar/leftsidebar";
import LeftResponsivesidebar from "../sidebar/left-responsive";
import Headmenu from "../sidebar/headmenu";
import CreateAdSet from "./CreateAdSet";
import { createAdSet, createAdSetTargeting, getCampaign, getAdAccount } from "../../hook/useAdsApi";
import { getCurrentUserId, AGE_MIN, AGE_MAX } from "./adsConstants";

export default function CreateAdSetPage() {
  const { campaignId } = useParams();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    adSetName: "",
    targetType: "profile",
    targetId: getCurrentUserId(),
    gender: "all",
    minAge: AGE_MIN,
    maxAge: AGE_MAX,
    interests: [],
    devices: "all",
    connectToSeller: false,
    previousBrandBuyers: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [adAccount, setAdAccount] = useState(null);

  // getCampaign() doesn't expose the ad account's seller_id directly (only used for the
  // ownership check server-side), so this fetches the account separately - needed to gate
  // the "Connection to Seller" toggle the same way the main wizard does.
  useEffect(() => {
    getCampaign(campaignId)
      .then((campaign) => getAdAccount(campaign.ad_account_id))
      .then(setAdAccount)
      .catch(() => setAdAccount(null));
  }, [campaignId]);

  const updateFormData = (patch) => setFormData((prev) => ({ ...prev, ...patch }));

  const handleCreate = async () => {
    setSubmitting(true);
    try {
      // Created as Active immediately - AdSet has no review process of its own, and nothing
      // in this UI ever transitions it out of the server's Draft default otherwise.
      const adSet = await createAdSet(campaignId, {
        name: formData.adSetName,
        target_type: formData.targetType,
        target_id: formData.targetId || undefined,
        status: "Active",
      });

      try {
        await createAdSetTargeting(adSet.id, {
          min_age: formData.minAge,
          max_age: formData.maxAge,
          genders: [formData.gender],
          // Unlike genders, the backend's device list has no "all" value - omitting the field
          // entirely means "no device restriction", which is what "All" is meant to convey.
          ...(formData.devices !== "all" && { devices: [formData.devices] }),
          interest_ids: formData.interests.map((i) => i.id),
          seller_relationship: { followers: formData.connectToSeller },
          previous_brand_buyers: formData.previousBrandBuyers,
        });
      } catch (targetingError) {
        // The ad set already exists at this point - don't block navigation over the
        // targeting save failing, just surface a warning.
        toast.warning(targetingError.response?.data?.message || "Failed to save ad set targeting");
      }

      toast.success("Ad set created");
      navigate(`/ads/ad-sets/${adSet.id}`);
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to create ad set");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="dashboard w-full min-h-screen flex">
      <Leftsidebar />
      <main className="flex-1 p-6 pl-8">
        <LeftResponsivesidebar />
        <Headmenu />
        <main className="flex">
          <div className="w-full max-w-2xl">
            <div className="flex items-center gap-3 mb-6">
              <button onClick={() => navigate(-1)} className="rounded-full bg-[#e7f3ea] p-2">
                ←
              </button>
              <h1 className="text-xl font-bold">Create Ad Set</h1>
            </div>
            <CreateAdSet
              formData={formData}
              updateFormData={updateFormData}
              onNext={handleCreate}
              onBack={() => navigate(-1)}
              continueLabel="Create Ad Set"
              submitting={submitting}
              adAccount={adAccount}
            />
          </div>
        </main>
      </main>
    </div>
  );
}
